import { useId, type ReactNode } from "react"

import { cn } from "~/utils/helpers"

/**
 * Double-container card for one page section: a bg-component shell carrying a fixed 44px header
 * (title, badges, meta, actions) around an unpadded bg-base body. Hugs its content, so beside a
 * taller card it keeps its own height instead of stretching into an empty shell.
 * Canonical intent: {@link ../design-system/intents.ts SECTION_CARD_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const SectionCard = ({
  title,
  badges,
  meta,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title: string
  badges?: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) => {
  const titleId = useId()
  const shownTitle = title.trim()
  return (
    <section
      aria-labelledby={shownTitle ? titleId : undefined}
      className={cn(
        "flex h-fit flex-col overflow-hidden rounded-[12px] bg-component shadow-card-rest",
        className,
      )}
    >
      {/* Fixed 44px with or without actions, so headers line up across cards. As the card narrows,
          meta truncates first but stays (a matrix's axis names live there even in 342px cards);
          badges shrink (a badge truncates its own text), the title keeps its full width while it
          fits and truncates last, and the actions give way after that. */}
      <div className="@container flex h-11 shrink-0 items-center gap-2 pl-4 pr-3">
        <div className="flex min-w-20 flex-1 items-center gap-2 overflow-hidden">
          {shownTitle && (
            <h3
              id={titleId}
              title={shownTitle}
              className="header-h3 text-fg-base max-w-full min-w-20 shrink-0 truncate"
            >
              {shownTitle}
            </h3>
          )}
          {badges && <div className="flex min-w-0 items-center gap-1">{badges}</div>}
          {meta && (
            <span
              title={typeof meta === "string" ? meta : undefined}
              className="paragraph-sm text-fg-muted min-w-0 truncate"
            >
              {meta}
            </span>
          )}
        </div>
        {actions && <div className="flex min-w-0 items-center gap-2">{actions}</div>}
      </div>
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-[12px] bg-base shadow-card-rest",
          bodyClassName,
        )}
      >
        {children}
      </div>
    </section>
  )
}
